import { memo, useCallback, useEffect, useState, type CSSProperties } from "react";
import styled from "styled-components";

import { CoordinateIcon, RotateCcwIcon } from "../../icons/glyphs";
import { AnchoredPopup, useAnchoredPopupAnchor } from "../AnchoredPopup";
import { Field } from "../Field";
import { TooltipIconButton } from "../IconButton";
import { NumberInput } from "../Input";
import { Text, defaultTextVariants } from "../Text";
import { AnnotationPointMarkerGlyph } from "./AnnotationPointMarkerGlyph";
import type { CoordinatePointPreview } from "./CapturedFrameViewport";
import { resolveHistoryShortcutModifier } from "./annotationShortcuts";
import { annotationStudioText } from "./messages";
import type { AnnotationPointKind, AnnotationStudioLocale } from "./types";

export interface AnnotationCoordinateOverlayState {
  readonly guideVisible: boolean;
  readonly preview: CoordinatePointPreview | null;
}

export const EMPTY_ANNOTATION_COORDINATE_OVERLAY: AnnotationCoordinateOverlayState = {
  guideVisible: false,
  preview: null,
};

const Root = styled.div`
  display: inline-flex;
  min-width: 0;
  align-items: center;
  gap: 0.2rem;
`;

const PointHistoryActions = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 0.1rem;
`;

const RedoIcon = styled(RotateCcwIcon)`
  transform: scaleX(-1);
`;

const CoordinatePointButton = styled(TooltipIconButton)`
  color: var(--c6-annotation-target-color);

  &:not(:disabled):hover,
  &:not(:disabled):active {
    color: var(--c6-annotation-target-color);
  }
`;

const CoordinateActions = styled.div`
  display: grid;
  min-width: 0;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 0.45rem;
`;

const ButtonRow = styled.div`
  display: flex;
  min-width: 0;
  flex-wrap: wrap;
  align-items: center;
  justify-content: flex-start;
  gap: 0.35rem;
  margin-top: 0.5rem;
`;

const PopupTitle = styled(Text)`
  font-weight: ${({ theme }) => theme.typography.weight.semibold};
`;

function AnnotationPointActionsComponent({
  locale,
  interactionDisabled,
  selectedTargetId,
  selectedTargetColor,
  canUndo,
  canRedo,
  onCoordinateOverlayChange,
  onAddPoint,
  onUndo,
  onRedo,
}: {
  locale: AnnotationStudioLocale;
  interactionDisabled: boolean;
  selectedTargetId: string | null;
  selectedTargetColor: string;
  canUndo: boolean;
  canRedo: boolean;
  onCoordinateOverlayChange: (state: AnnotationCoordinateOverlayState) => void;
  onAddPoint: (kind: AnnotationPointKind, x: number, y: number) => void;
  onUndo: () => void;
  onRedo: () => void;
}) {
  const coordinateInput = useAnchoredPopupAnchor();
  const [x, setX] = useState<number | null>(0.5);
  const [y, setY] = useState<number | null>(0.5);
  const [hoveredPointKind, setHoveredPointKind] = useState<AnnotationPointKind | null>(null);
  const [focusedPointKind, setFocusedPointKind] = useState<AnnotationPointKind | null>(null);
  const historyShortcutModifier = resolveHistoryShortcutModifier();
  const undoShortcutLabel = `${historyShortcutModifier} + Z`;
  const redoShortcutLabel = `${historyShortcutModifier} + Shift + Z`;
  const previewKind = hoveredPointKind ?? focusedPointKind;
  const closePopup = coordinateInput.close;
  const closeCoordinateInput = useCallback(() => {
    closePopup();
    setHoveredPointKind(null);
    setFocusedPointKind(null);
  }, [closePopup]);

  useEffect(() => {
    if (interactionDisabled) closeCoordinateInput();
  }, [closeCoordinateInput, interactionDisabled]);

  useEffect(() => {
    const preview =
      !interactionDisabled &&
      coordinateInput.open &&
      selectedTargetId !== null &&
      x !== null &&
      y !== null &&
      Number.isFinite(x) &&
      Number.isFinite(y)
        ? { x, y, color: selectedTargetColor, kind: previewKind }
        : null;
    onCoordinateOverlayChange({
      guideVisible: coordinateInput.open,
      preview,
    });
  }, [
    coordinateInput.open,
    interactionDisabled,
    onCoordinateOverlayChange,
    previewKind,
    selectedTargetColor,
    selectedTargetId,
    x,
    y,
  ]);

  useEffect(
    () => () => onCoordinateOverlayChange(EMPTY_ANNOTATION_COORDINATE_OVERLAY),
    [onCoordinateOverlayChange],
  );

  return (
    <Root data-point-input-toolbar="">
      <TooltipIconButton
        data-coordinate-input-action=""
        size="sm"
        variant="surface"
        border={false}
        aria-label={annotationStudioText("coordinateInput", locale)}
        tooltip={{
          content: annotationStudioText("coordinateInput", locale),
          placement: "bottom",
        }}
        selected={coordinateInput.open}
        aria-haspopup="dialog"
        aria-expanded={coordinateInput.open}
        onClick={(event) => {
          if (coordinateInput.open) closeCoordinateInput();
          else coordinateInput.openFromElement(event);
        }}
      >
        <CoordinateIcon />
      </TooltipIconButton>
      <PointHistoryActions
        role="group"
        aria-label={annotationStudioText("annotationEditHistory", locale)}
        data-point-history-actions=""
      >
        <TooltipIconButton
          size="sm"
          variant="surface"
          border={false}
          aria-label={annotationStudioText("undoAnnotation", locale)}
          aria-keyshortcuts="Control+Z Meta+Z"
          tooltip={{
            content: `${annotationStudioText("undoAnnotation", locale)} · ${undoShortcutLabel}`,
            placement: "bottom",
          }}
          disabled={!canUndo}
          onClick={onUndo}
        >
          <RotateCcwIcon />
        </TooltipIconButton>
        <TooltipIconButton
          size="sm"
          variant="surface"
          border={false}
          aria-label={annotationStudioText("redoAnnotation", locale)}
          aria-keyshortcuts="Control+Shift+Z Meta+Shift+Z Control+Y"
          tooltip={{
            content: `${annotationStudioText("redoAnnotation", locale)} · ${redoShortcutLabel}`,
            placement: "bottom",
          }}
          disabled={!canRedo}
          onClick={onRedo}
        >
          <RedoIcon />
        </TooltipIconButton>
      </PointHistoryActions>
      <AnchoredPopup
        open={coordinateInput.open}
        anchor={coordinateInput.anchor}
        onClose={closeCoordinateInput}
        placement="bottom-end"
        width="18rem"
        returnFocus
        aria-label={annotationStudioText("coordinateInput", locale)}
      >
        <AnchoredPopup.Header divider={false}>
          <PopupTitle variant={defaultTextVariants["caption-bold"]}>
            {annotationStudioText("coordinateInput", locale)}
          </PopupTitle>
        </AnchoredPopup.Header>
        <AnchoredPopup.Body padding="md" scroll={false}>
          <CoordinateActions>
            <Field label={annotationStudioText("xCoordinate", locale)} layout="stack">
              <NumberInput
                value={x}
                min={0}
                max={1}
                maxDecimalPlaces={3}
                allowNegative={false}
                onChange={(value) => setX(value === null ? null : Math.max(0, Math.min(1, value)))}
              />
            </Field>
            <Field label={annotationStudioText("yCoordinate", locale)} layout="stack">
              <NumberInput
                value={y}
                min={0}
                max={1}
                maxDecimalPlaces={3}
                allowNegative={false}
                onChange={(value) => setY(value === null ? null : Math.max(0, Math.min(1, value)))}
              />
            </Field>
          </CoordinateActions>
          <ButtonRow
            style={
              {
                "--c6-annotation-target-color": selectedTargetColor,
              } as CSSProperties
            }
          >
            <CoordinatePointButton
              size="sm"
              variant="ghost"
              aria-label={annotationStudioText("includePoint", locale)}
              tooltip={{
                content: annotationStudioText("includePoint", locale),
                placement: "bottom",
              }}
              onPointerEnter={() => setHoveredPointKind("include")}
              onPointerLeave={() => setHoveredPointKind(null)}
              onFocus={() => setFocusedPointKind("include")}
              onBlur={() => setFocusedPointKind(null)}
              onClick={() => {
                if (x !== null && y !== null) onAddPoint("include", x, y);
                closeCoordinateInput();
              }}
            >
              <AnnotationPointMarkerGlyph kind="include" variant="control" />
            </CoordinatePointButton>
            <CoordinatePointButton
              size="sm"
              variant="ghost"
              aria-label={annotationStudioText("excludePoint", locale)}
              tooltip={{
                content: annotationStudioText("excludePoint", locale),
                placement: "bottom",
              }}
              onPointerEnter={() => setHoveredPointKind("exclude")}
              onPointerLeave={() => setHoveredPointKind(null)}
              onFocus={() => setFocusedPointKind("exclude")}
              onBlur={() => setFocusedPointKind(null)}
              onClick={() => {
                if (x !== null && y !== null) onAddPoint("exclude", x, y);
                closeCoordinateInput();
              }}
            >
              <AnnotationPointMarkerGlyph kind="exclude" variant="control" />
            </CoordinatePointButton>
          </ButtonRow>
        </AnchoredPopup.Body>
      </AnchoredPopup>
    </Root>
  );
}

export const AnnotationPointActions = memo(AnnotationPointActionsComponent);
